/**
 * Milestone placement — inline slot detection vs chain-scoped tidy fallback.
 */

import { buildDependencyGraph } from '../layout/graphBuilder';
import { findDisconnectedComponents } from '../layout';
import { expandEdgesThroughMilestones } from './edgePaths/edgeGeneration';
import {
  MILESTONE_NODE_WIDTH,
  MILESTONE_NODE_HEIGHT,
  MILESTONE_JUNCTION_SIZE,
  getMilestoneAnchor,
  getMilestoneJunctionPosition,
  getMilestoneLayoutDims,
  isMilestoneNode,
} from './milestoneUtils';

const DEFAULT_HOST_WIDTH = 290;
const DEFAULT_HOST_HEIGHT = 150;
const COLLISION_PADDING = 6;

function getTaskLayoutDims(task, nodeDimensions) {
  if (isMilestoneNode(task)) {
    return getMilestoneLayoutDims(nodeDimensions, task.id);
  }
  const d = nodeDimensions?.[task.id];
  return {
    width: d?.width ?? DEFAULT_HOST_WIDTH,
    height: d?.height ?? DEFAULT_HOST_HEIGHT,
  };
}

function toRect(left, top, width, height) {
  return { left, top, right: left + width, bottom: top + height };
}

function rectsOverlap(a, b, padding = COLLISION_PADDING) {
  return (
    a.left < b.right + padding
    && a.right + padding > b.left
    && a.top < b.bottom + padding
    && a.bottom + padding > b.top
  );
}

/** Label flips to the left when the host mission sits mostly to the right. */
export function shouldMilestoneLabelOnLeft(layoutPos, hostTask, hostDims) {
  const hostPos = hostTask?._placementPos;
  const hx = hostPos?.x ?? hostTask?.x;
  if (!Number.isFinite(hx)) return false;
  const hostWidth = hostDims?.width ?? DEFAULT_HOST_WIDTH;
  const layoutCenterX = layoutPos.x + MILESTONE_NODE_WIDTH / 2;
  const hostCenterX = hx + hostWidth / 2;
  return hostCenterX > layoutCenterX + 24;
}

/** Merge persisted task coords with live canvas positions (custom / ELK display). */
export function buildNodePositionsFromTasks(tasks, displayPositions = {}) {
  const positions = {};
  (tasks || []).forEach((task) => {
    const live = displayPositions[task.id];
    if (live && Number.isFinite(live.x) && Number.isFinite(live.y)) {
      positions[task.id] = { x: live.x, y: live.y };
    } else if (Number.isFinite(task.x) && Number.isFinite(task.y)) {
      positions[task.id] = { x: task.x, y: task.y };
    }
  });
  return positions;
}

/** Place the footprint so the junction (default right-label edge) lands on a midpoint. */
export function getMilestoneJunctionPositionOnMidpoint(midpoint) {
  if (!midpoint || !Number.isFinite(midpoint.x) || !Number.isFinite(midpoint.y)) {
    return { x: 0, y: 0 };
  }
  return {
    x: midpoint.x - MILESTONE_JUNCTION_SIZE / 2,
    y: midpoint.y - MILESTONE_NODE_HEIGHT / 2,
  };
}

/**
 * Host mission for an output-side milestone on a dependency segment.
 * Uses edge.from when it is a mission; otherwise walks upstream past pass-through milestones.
 */
export function resolveOutputMilestoneHostFromEdge(edge, tasks) {
  if (!edge?.from || !tasks?.length) return null;
  const taskMap = new Map(tasks.map((t) => [t.id, t]));
  let id = edge.from;
  const fromTask = taskMap.get(id);
  if (!fromTask) return null;
  if (!isMilestoneNode(fromTask)) return id;

  const anchor = getMilestoneAnchor(fromTask);
  if (anchor?.side === 'output') return anchor.hostTaskId;

  const segments = expandEdgesThroughMilestones(tasks);
  const incoming = segments.filter((s) => s.to === id);
  for (const seg of incoming) {
    const upstream = taskMap.get(seg.from);
    if (upstream && !isMilestoneNode(upstream)) return seg.from;
  }

  return anchor?.hostTaskId ?? null;
}

/** Full layout bounding box used for collision checks (matches ELK / edge routing). */
export function getMilestoneOccupiedBounds(layoutPos, _labelOnLeft) {
  return toRect(layoutPos.x, layoutPos.y, MILESTONE_NODE_WIDTH, MILESTONE_NODE_HEIGHT);
}

/** All task ids in the same disconnected chain as the host (includes milestones). */
export function getChainTaskIdsForHost(hostTaskId, tasks) {
  if (!hostTaskId || !tasks?.length) return new Set([hostTaskId].filter(Boolean));
  const graph = buildDependencyGraph(tasks);
  const components = findDisconnectedComponents(tasks, graph);
  const component = components.find((c) => c.tasks.some((t) => t.id === hostTaskId));
  if (!component) return new Set([hostTaskId]);
  return new Set(component.tasks.map((t) => t.id));
}

/**
 * Pick layout position above/below host; report whether it fits without shifting other nodes.
 */
export function resolveMilestonePlacement({
  hostTask,
  side,
  tasks,
  positions,
  nodeDimensions = {},
  existingSameSide = [],
  positionOverride = null,
}) {
  const hostPos = positions[hostTask.id];
  const hostDims = getTaskLayoutDims(hostTask, nodeDimensions);
  const hostForHeuristic = hostPos ? { ...hostTask, _placementPos: hostPos } : hostTask;

  const position = positionOverride
    ?? (hostPos
      ? getMilestoneJunctionPosition(hostPos, hostDims, side, existingSameSide.length)
      : getMilestoneJunctionPosition(
        { x: hostTask.x ?? 0, y: hostTask.y ?? 0 },
        hostDims,
        side,
        existingSameSide.length,
      ));

  const excludeIds = new Set([
    hostTask.id,
    ...existingSameSide.map((m) => m.id),
  ]);

  const labelOnLeft = shouldMilestoneLabelOnLeft(position, hostForHeuristic, hostDims);
  const milestoneBounds = getMilestoneOccupiedBounds(position, labelOnLeft);

  let fitsInline = true;

  if (!hostPos) {
    fitsInline = false;
  } else {
    const hostRect = toRect(hostPos.x, hostPos.y, hostDims.width, hostDims.height);
    if (rectsOverlap(milestoneBounds, hostRect)) {
      fitsInline = false;
    }
  }

  if (fitsInline && hostPos) {
    for (const task of tasks) {
      if (excludeIds.has(task.id)) continue;
      const pos = positions[task.id];
      if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) continue;
      const dims = getTaskLayoutDims(task, nodeDimensions);
      const taskRect = toRect(pos.x, pos.y, dims.width, dims.height);
      if (rectsOverlap(milestoneBounds, taskRect)) {
        fitsInline = false;
        break;
      }
    }
  }

  return {
    fitsInline,
    position,
    chainTaskIds: getChainTaskIdsForHost(hostTask.id, tasks),
  };
}

/**
 * Clear runtime overrides and run partial/full tidy for a chain after adding a milestone.
 */
export function scheduleChainTidyAfterMilestoneAdd(flowchartRef, chainTaskIds, newTaskId, options = {}) {
  const ids = new Set(chainTaskIds);
  if (newTaskId) ids.add(newTaskId);

  setTimeout(() => {
    flowchartRef?.current?.clearCustomPositionsForNodeIds?.(ids);
    setTimeout(() => {
      flowchartRef?.current?.autoLayout?.(ids, { skipRecordAction: options.skipRecordAction ?? true });
    }, 50);
  }, 50);
}
